import { useCallback, useEffect, useState } from 'react';

/** Default debounce delay (3 seconds) to avoid spamming API requests. */
const DEBOUNCE_MS = 3000;

/**
 * Returns a debounced value that updates after the source stops changing,
 * plus a flush callback to apply the current value immediately (e.g. on Enter).
 *
 * @param value - The source value to debounce
 * @param delayMs - Delay in ms before updating (default 3000)
 * @returns [debouncedValue, flush] - flush applies the current value immediately
 */
export function useDebouncedValue<T>(
  value: T,
  delayMs = DEBOUNCE_MS,
): [T, () => void] {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delayMs);

    return () => clearTimeout(timer);
  }, [value, delayMs]);

  const flush = useCallback(() => {
    setDebouncedValue(value);
  }, [value]);

  return [debouncedValue, flush];
}
